import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/db';
import SafeIcon from '@/common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const GdprModal = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2rem] w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="text-xl font-bold text-gray-900">Adatkezelési Tájékoztató</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><SafeIcon name="X" className="w-6 h-6 text-gray-500" /></button>
        </div>
        <div className="p-8 max-h-[70vh] overflow-y-auto text-gray-600 leading-relaxed text-sm space-y-4">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 space-y-2">
            <p className="font-bold text-blue-900">Az adatkezelő adatai:</p>
            <p><strong>Cégnév:</strong> {data.company_name || 'Nincs megadva'}</p>
            <p><strong>E-mail:</strong> {data.email || 'Nincs megadva'}</p>
            <p><strong>Telefon:</strong> {data.phone || 'Nincs megadva'}</p>
          </div>
          <p className="font-bold text-gray-900">Adatkezelés célja:</p>
          <p>A megadott adatokat kizárólag a vásárlói visszajelzések feldolgozása céljából tároljuk.</p>
          <p className="font-bold text-gray-900">Jogalap:</p>
          <p>Az adatkezelés az Ön önkéntes hozzájárulásán alapul.</p>
          <p className="text-xs text-gray-400 pt-4 border-t border-gray-100">Ön bármikor kérheti adatai törlését a fenti elérhetőségek bármelyikén.</p>
        </div>
        <div className="p-6 bg-gray-50 text-center border-t border-gray-100">
          <button onClick={onClose} className="px-8 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-colors">Értem</button>
        </div>
      </motion.div>
    </div>
  );
};

const ReviewPage = () => {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [showGdpr, setShowGdpr] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '', consent: false });
  // Animáció 4-5 csillaghoz
  const [showSuccessAnim, setShowSuccessAnim] = useState(false);
  const [animStars, setAnimStars] = useState(0);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const data = await api.getLocationBySlug(slug);
        setLocation(data);
        document.title = `${data.name} - ${data.headline}`;
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchLocation();
    return () => { document.title = 'SocialCard Review'; };
  }, [slug]);

  const handleStarClick = async (rating) => {
    if (submitted) return;
    setStars(rating);
    await api.logEvent({ location_id: location.id, event_type: 'star_click', star_rating: rating });
    if (rating >= 4) {
      // Animáció indítása
      setShowSuccessAnim(true);
      for (let i = 1; i <= 5; i++) {
        setTimeout(() => setAnimStars(i), i * 120);
      }
      await api.logEvent({ location_id: location.id, event_type: 'google_redirect', star_rating: rating });
      // 1 mp után átirányítás Google-ra
      setTimeout(() => { window.location.href = location.google_review_url; }, 1000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) return alert("Kérjük, fogadja el az adatkezelési nyilatkozatot.");
    await api.submitReview({
      location_id: location.id,
      name: formData.name, email: formData.email,
      star_rating: stars, message: formData.message,
      gdpr_consent: formData.consent
    });
    await api.logEvent({ location_id: location.id, event_type: 'form_submit', star_rating: stars });
    setSubmitted(true);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900"><div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div></div>;
  if (!location) return <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-gray-50 dark:bg-gray-900"><h2 className="text-2xl font-bold mb-4 dark:text-white">Helyszín nem található</h2><Link to="/" className="px-6 py-2 bg-blue-600 text-white rounded-full">Vissza</Link></div>;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4 transition-colors">
      <GdprModal isOpen={showGdpr} onClose={() => setShowGdpr(false)} data={location} />

      {/* ─── 4-5 csillag siker animáció ─────────────────────────────── */}
      {showSuccessAnim && (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-white"
          style={{ animation: 'scFadeIn .2s ease-out' }}>
          <style>{`
            @keyframes scFadeIn { from { opacity:0 } to { opacity:1 } }
            @keyframes scStarPop {
              0%   { transform: scale(0) rotate(-25deg); opacity:0 }
              65%  { transform: scale(1.35) rotate(8deg); opacity:1 }
              100% { transform: scale(1) rotate(0deg); opacity:1 }
            }
            .sc-star { opacity:0 }
            .sc-star-on { animation: scStarPop .35s cubic-bezier(.34,1.56,.64,1) forwards }
            @keyframes scTextIn { from { transform:translateY(14px); opacity:0 } to { transform:none; opacity:1 } }
            .sc-text { animation: scTextIn .4s .68s ease-out both }
          `}</style>
          <div className="flex gap-3 mb-6">
            {[1,2,3,4,5].map(i => (
              <span key={i} className={animStars >= i ? 'sc-star-on' : 'sc-star'}
                style={{ fontSize: 56, display: 'inline-block', animationDelay: `${(i-1)*0.12}s` }}>⭐</span>
            ))}
          </div>
          <p className="sc-text text-2xl font-black text-gray-900">Köszönjük az értékelést!</p>
          <p className="sc-text text-sm text-gray-400 mt-2">Átirányítás a Google értékeléshez…</p>
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="h-4 w-full" style={{ backgroundColor: location.accent_color }}></div>
        <div className="p-8 md:p-12">
          {/* LOGO MEGJELENÍTÉSE */}
          {location.logo_url && (
            <div className="flex justify-center mb-8">
              <img src={location.logo_url} alt="Logo" className="h-16 w-auto object-contain" />
            </div>
          )}

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce"><SafeIcon name="Check" className="w-12 h-12 text-green-600" /></div>
                <h2 className="text-3xl font-black text-gray-900 mb-4">{location.success_headline}</h2>
                <p className="text-lg text-gray-600">{location.success_message}</p>
              </motion.div>
            ) : (
              <motion.div key="form">
                <div className="text-center mb-10">
                  <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-4 tracking-tight leading-tight">{location.headline}</h1>
                  <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">{location.description}</p>
                </div>

                <div className="flex justify-center gap-2 md:gap-4 mb-10">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button key={star} onClick={() => handleStarClick(star)} onMouseEnter={() => setHoverStars(star)} onMouseLeave={() => setHoverStars(0)} className="focus:outline-none transition-transform active:scale-90 hover:scale-110">
                      <SafeIcon name="Star" className={`w-12 h-12 md:w-16 md:h-16 transition-all duration-300 ${(hoverStars || stars) >= star ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_12px_rgba(250,204,21,0.6)]' : 'fill-gray-100 text-gray-200'}`} />
                    </button>
                  ))}
                </div>

                {stars > 0 && stars < 4 && (
                  <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="space-y-5">
                    <div className="p-5 bg-amber-50 rounded-2xl border border-amber-100 text-sm text-amber-900 font-bold flex items-center gap-2">
                        <SafeIcon name="Info" className="w-4 h-4" /> {location.negative_prompt}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input required className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-blue-500" placeholder={location.placeholder_name} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                      <input required type="email" className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-blue-500" placeholder={location.placeholder_email} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <textarea required rows={3} className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-2xl px-5 py-4 outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder={location.placeholder_message} value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} />
                    <div className="flex items-start gap-3 px-2">
                      <input required type="checkbox" id="gdpr" className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600" checked={formData.consent} onChange={e => setFormData({...formData, consent: e.target.checked})} />
                      <label htmlFor="gdpr" className="text-xs text-gray-500 leading-tight">
                        Elfogadom az adatkezelési szabályzatot. <button type="button" onClick={() => setShowGdpr(true)} className="ml-1 text-blue-600 font-bold underline">Részletek</button>
                      </label>
                    </div>
                    <button type="submit" className="w-full py-5 rounded-2xl text-white font-black text-xl shadow-xl hover:brightness-110 active:scale-[0.98] transition-all" style={{ backgroundColor: location.accent_color }}>
                      {location.button_text}
                    </button>
                  </motion.form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ReviewPage;